import React from 'react'

import {isEqual, omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'
import * as Yup from 'yup'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {setProvider} from '../../redux/features/core'
import {openChangePasswordModal, openEditProviderEmailsModal} from '../../redux/features/modal'
import {getInitialStatusFilter, setStatusFilter} from '../../redux/features/order'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as localStorage from '../../../../commons/src/services/local-storage'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import {formatCurrency, formatPrice} from '../../libs/formatter'
import {calculatePricesForMinProductionPrice} from '../../libs/pricing'
import {anyPermissionExceptOperator} from '../../libs/user'
import * as providerApi from '../../services/api/provider'
import * as providerEmailApi from '../../services/api/provider-email'
import {captureException} from '../../services/logging'
import {toNumberValue, toStringValue, toTouchedFormData} from '../utils/form'
import {isPermitted} from '../utils/routing'

import {Permission, Provider} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import emailIcon from '../../../../commons/src/assets/icons/email.svg'
import lockIcon from '../../../../commons/src/assets/icons/lock.svg'

import Button from '../../../../commons/src/components/button'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import InputField from '../../../../commons/src/components/input-field'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox-field'
import LinkButton from '../../../../commons/src/components/link-button'
import Link from '../../../../commons/src/components/link/Link'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import {orderStatusTypeToContentMap} from '../../components/order-status-badge/OrderStatusBadge'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'

import {OrderStatusType} from '../../order-types'
import RequireAuthPartial from '../require-auth-partial'

export type SingleFormData = {
  minimumProductionPrice: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

const validationSchema = {
  minimumProductionPrice: Yup.number().min(1).label('Minimum production price'),
}

const initialValues = {
  minimumProductionPrice: '',
}

const providerToFormData = (provider: Provider) => ({
  minimumProductionPrice: provider.minimumProductionPrice
    ? toStringValue(provider.minimumProductionPrice)
    : '',
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  minimumProductionPrice: toNumberValue,
}

const formDataToPartialRequest = (formData: Partial<SingleFormData>) => {
  return {
    minimumProductionPrice: formDataTransforms.minimumProductionPrice(
      formData.minimumProductionPrice
    ),
  }
}

const SettingsGeneralPartial = () => {
  const dispatch = useAppDispatch()

  const user = useAppSelector(state => state.auth.user)
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const [defaultOrderStatusFiltersSubmitted, setDefaultOrderStatusFiltersSubmitted] =
    React.useState(false)
  const [defaultOrderStatusFilters, setDefaultOrderStatusFilters] = React.useState(
    localStorage.getItem(config.localStorageKeys.defaultStatusFilters) ||
      config.defaultStatusFilters
  )

  const {
    isLoading: isLoadingProviderContactEmails,
    data: providerContactEmails = [],
    refresh: receiveProviderContactEmails,
    error: providerContactEmailError,
  } = useAsync(() => providerEmailApi.getByProvider(user?.permission, provider.id), [], {
    isEnabled: !!user && isPermitted(user.permission, [Permission.PROVIDER]),
    onError: error => {
      captureException(error)
    },
  })

  const minProdFormik = useFormik<SingleFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...providerToFormData(provider),
    },
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerRequest = formDataToPartialRequest(touchedValues)

      try {
        const nextProvider = await providerApi.patch(providerRequest, provider.id)

        setIsSubmitted(true)
        dispatch(setProvider({provider: nextProvider}))
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const pricings = calculatePricesForMinProductionPrice(
    minProdFormik.values.minimumProductionPrice
      ? parseFloat(minProdFormik.values.minimumProductionPrice)
      : 0,
    config.priceCalculation.craftcloudMargin,
    config.priceCalculation.profitShare,
    config.priceCalculation.modelFee[
      provider.currency as keyof typeof config.priceCalculation.modelFee
    ] || config.priceCalculation.modelFee.USD
  )

  const finalCustomerPrice = formatPrice(pricings.totalCustomerPriceV2, provider.currency)

  const yourEarningsAmount = formatPrice(pricings.invoiceableAmountV2, provider.currency)

  const renderDefaultOrderStatusFilterSettings = () => {
    const orderStatusOptions = omit(orderStatusTypeToContentMap, [OrderStatusType.UNKNOWN])
    const localStorageAvailable = localStorage.hasLocalStorage

    return (
      <Section>
        <Stack>
          <Headline label="Default order status filter" className="u-no-margin-bottom" />
          <Stack space="60">
            <Paragraph className="u-no-margin-bottom">
              You can set your preferred default status filter values for the order table here.
            </Paragraph>
            <Paragraph minor size="s" className="u-no-margin-bottom">
              Note: Your preferences are stored in your browser&apos;s local storage and are not
              tied to your account.
            </Paragraph>
          </Stack>
          {!localStorageAvailable && (
            <Paragraph warning className="u-no-margin-bottom">
              This feature is disabled because we cannot access the browsers local storage.
            </Paragraph>
          )}
          <div>
            {Object.keys(orderStatusOptions).map((key: string) => {
              const type = key as OrderStatusType
              const option = orderStatusTypeToContentMap[type]
              return (
                <LabeledCheckbox
                  key={option.type}
                  label={option.label}
                  checked={defaultOrderStatusFilters.includes(type)}
                  onChange={value => {
                    setDefaultOrderStatusFiltersSubmitted(false)
                    if (value) {
                      setDefaultOrderStatusFilters([...defaultOrderStatusFilters, type])
                    } else {
                      setDefaultOrderStatusFilters(
                        defaultOrderStatusFilters.filter(
                          (filterValue: OrderStatusType) => filterValue !== type
                        )
                      )
                    }
                  }}
                  disabled={!localStorageAvailable}
                />
              )
            })}
          </div>
          <Button
            className="u-margin-top-base"
            disabled={
              !localStorageAvailable ||
              isEqual(
                localStorage.getItem(config.localStorageKeys.defaultStatusFilters),
                defaultOrderStatusFilters
              )
            }
            icon={defaultOrderStatusFiltersSubmitted ? <Icon source={checkedIcon} /> : undefined}
            onClick={preventDoubleClick(() => {
              localStorage.setItem(
                config.localStorageKeys.defaultStatusFilters,
                defaultOrderStatusFilters
              )
              setDefaultOrderStatusFiltersSubmitted(true)
              // reset status filters in state
              dispatch(setStatusFilter({statusFilter: getInitialStatusFilter()}))
            })}
          >
            Save
          </Button>
        </Stack>
      </Section>
    )
  }

  return (
    <>
      <Section>
        <Headline label="Your Login E-mail" />
        <InputField
          disabled
          icon={<Icon source={emailIcon} />}
          value={user?.attributes?.email || ''}
          placeholder="E-Mail"
          className="u-margin-bottom-l"
        />
        <Headline label="Your Password" />
        <InputField
          disabled
          icon={<Icon source={lockIcon} />}
          value="••••••••••"
          placeholder="Password"
          className="u-margin-bottom"
        />
        <Button hierarchy="secondary" onClick={() => dispatch(openChangePasswordModal())}>
          Change password
        </Button>
      </Section>
      <RequireAuthPartial requiredPermissions={[Permission.PROVIDER]}>
        <Section>
          <SplitLayout
            left={<Headline label="Your Contact Email Addresses" />}
            right={
              <LinkButton
                size="200"
                icon={<Icon source={editIcon} title="Edit" />}
                disabled={isLoadingProviderContactEmails || !!providerContactEmailError}
                onClick={() =>
                  dispatch(
                    openEditProviderEmailsModal({
                      providerEmails: providerContactEmails,
                      onDone: receiveProviderContactEmails,
                    })
                  )
                }
              />
            }
          />
          <Table
            empty={
              providerContactEmailError ? (
                <Paragraph warning>
                  We were not able to load your contact emails.{' '}
                  <LinkButton
                    disabled={isLoadingProviderContactEmails}
                    onClick={receiveProviderContactEmails}
                  >
                    Please try again!
                  </LinkButton>
                </Paragraph>
              ) : (
                'No contact emails yet'
              )
            }
            isLoading={isLoadingProviderContactEmails}
            header={
              <TableRow>
                <TableHeaderCell width={4}>Email</TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.ordersDescription}</Tooltip>
                    }
                  >
                    <Link>Orders</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.invoiceDescription}</Tooltip>
                    }
                  >
                    <Link>Invoices</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    tooltip={
                      <Tooltip appearance="dark">
                        {config.providerEmail.messagesDescription}
                      </Tooltip>
                    }
                  >
                    <Link>Messages</Link>
                  </TooltipHandler>
                </TableHeaderCell>
                <TableHeaderCell width={1}>
                  <TooltipHandler
                    tooltip={
                      <Tooltip appearance="dark">{config.providerEmail.newsDescription}</Tooltip>
                    }
                  >
                    <Link>News</Link>
                  </TooltipHandler>
                </TableHeaderCell>
              </TableRow>
            }
          >
            {providerContactEmails.map(contact => (
              <TableRow key={contact.id}>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{contact.orders ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.invoices ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.messages ? <Icon source={checkedIcon} /> : '-'}</TableCell>
                <TableCell>{contact.news ? <Icon source={checkedIcon} /> : '-'}</TableCell>
              </TableRow>
            ))}
          </Table>
        </Section>
      </RequireAuthPartial>
      <RequireAuthPartial requiredPermissions={anyPermissionExceptOperator}>
        <Section>
          <Headline label="Minimum production cost" />
          <Paragraph>Set a minimum production cost that you will receive for an order.</Paragraph>
          <form onSubmit={minProdFormik.handleSubmit}>
            {minProdFormik.errors.formikGeneral && (
              <Paragraph error>{minProdFormik.errors.formikGeneral}</Paragraph>
            )}
            <FormRow layout="half-half">
              <FieldNote
                appearance="error"
                note={minProdFormik.submitCount > 0 && minProdFormik.errors.minimumProductionPrice}
                className="u-margin-bottom"
              >
                <NumberInputField
                  allowDecimals
                  allowOnly2Decimals
                  name="minimumProductionPrice"
                  placeholder="Min production cost"
                  value={minProdFormik.values.minimumProductionPrice}
                  appearance={
                    minProdFormik.submitCount > 0 && !!minProdFormik.errors.minimumProductionPrice
                      ? 'error'
                      : undefined
                  }
                  suffix={formatCurrency(provider.currency)}
                  onChange={(value, name) => {
                    minProdFormik.setFieldValue(name as string, value)
                    if (isSubmitted) {
                      setIsSubmitted(false)
                    }
                  }}
                  onBlur={minProdFormik.handleBlur}
                />
              </FieldNote>
              <Stack space="60">
                <Paragraph minor size="xs" className="u-no-margin-bottom">
                  Final Customer price:{' '}
                  {minProdFormik.values.minimumProductionPrice ? finalCustomerPrice : 'n/a'}{' '}
                  {minProdFormik.values.minimumProductionPrice ? '(1 model)' : null}
                </Paragraph>
                <Paragraph minor size="xs" className="u-no-margin-bottom">
                  Your earnings:{' '}
                  {minProdFormik.values.minimumProductionPrice ? yourEarningsAmount : 'n/a'}
                </Paragraph>
              </Stack>
            </FormRow>
          </form>
          <Button
            className="u-margin-top-base"
            disabled={minProdFormik.isSubmitting || isSubmitted || !minProdFormik.dirty}
            icon={
              isSubmitted ? (
                <Icon source={checkedIcon} />
              ) : minProdFormik.isSubmitting &&
                minProdFormik.submitCount > 0 &&
                !isEmpty(minProdFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => minProdFormik.handleSubmit())}
          >
            {isSubmitted ? 'Saved' : 'Save'}
          </Button>
        </Section>
      </RequireAuthPartial>
      {renderDefaultOrderStatusFilterSettings()}
    </>
  )
}

export default SettingsGeneralPartial
